/**
 * PATH: apps/qa/src/pages/ncr-detail/NCRCreateForm.tsx
 * PURPOSE: New NCR creation form with inspection/area selection
 */

import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ncrApi, inspectionApi, areaApi } from '@/lib/api'
import { cn } from '@/lib/utils'
import { ArrowLeft, Save, Loader2 } from 'lucide-react'
import { getSeverityConfig } from './helpers'

export function NCRCreateForm() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [severity, setSeverity] = useState<'low' | 'medium' | 'high' | 'critical'>('low')
  const [inspectionId, setInspectionId] = useState('')
  const [jobId, setJobId] = useState('')
  const [areaId, setAreaId] = useState('')

  const { data: inspectionsData } = useQuery({
    queryKey: ['qa-inspections-list'],
    queryFn: async () => inspectionApi.getInspections({ page_size: 100 } as Parameters<typeof inspectionApi.getInspections>[0]),
  })
  const inspections = inspectionsData?.results || []

  const { data: areasData, isLoading: areasLoading } = useQuery({
    queryKey: ['qa-areas-for-inspection', inspectionId],
    queryFn: () => areaApi.getAreas({ inspection_id: inspectionId, page_size: 500 }),
    enabled: !!inspectionId,
  })
  const areas = (areasData as Record<string, unknown>)?.results || []

  useEffect(() => {
    if (!inspectionId) { setJobId(''); setAreaId(''); return }
    const selected = inspections.find((i: Record<string, unknown>) => i.id === inspectionId)
    setJobId(selected?.job_id ? String(selected.job_id) : '')
    setAreaId('')
  }, [inspectionId, inspections])

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!inspectionId) throw new Error('Please select an inspection')
      if (!areaId) throw new Error('Please select an area')
      if (!jobId) throw new Error('Missing job for selected inspection')
      if (!title.trim()) throw new Error('Title is required')
      if (!description.trim()) throw new Error('Issue description is required')
      return ncrApi.createNCR({ title, issue_description: description, severity, job: jobId, inspection: inspectionId, area: areaId })
    },
    onSuccess: (data) => { queryClient.invalidateQueries({ queryKey: ['qa-ncrs'] }); navigate(`/ncrs/${data.id}`) },
  })

  const isPending = createMutation.isPending

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/ncrs')} className="p-2 rounded-lg hover:bg-accent transition-colors">
          <ArrowLeft className="w-5 h-5 text-muted-foreground" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">New NCR</h1>
          <p className="text-muted-foreground">Report a non-conformance or defect</p>
        </div>
      </div>

      <div className="bg-card rounded-xl shadow-sm border border-border p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Title <span className="text-destructive">*</span></label>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Brief description of the issue"
            className="w-full px-4 py-2.5 rounded-xl border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Description</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Detailed description of the non-conformance..." rows={4}
            className="w-full px-4 py-2.5 rounded-xl border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none" />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Severity</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {(['low', 'medium', 'high', 'critical'] as const).map((sev) => {
              const config = getSeverityConfig(sev)
              return (
                <button key={sev} onClick={() => setSeverity(sev)}
                  className={cn('px-4 py-2 rounded-xl border font-medium capitalize transition-all',
                    severity === sev ? `${config.bg} ${config.text} border-current` : 'border-input text-muted-foreground hover:bg-accent')}>
                  {sev}
                </button>
              )
            })}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Inspection <span className="text-destructive">*</span></label>
          <select value={inspectionId} onChange={(e) => setInspectionId(e.target.value)}
            className="w-full px-4 py-2.5 rounded-xl border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary">
            <option value="">Select inspection...</option>
            {inspections.map((inspection: Record<string, unknown>) => (
              <option key={inspection.id} value={inspection.id}>
                {(inspection.job_id ? `Job #${inspection.job_id} · ` : '')}{inspection.job_name || inspection.inspection_name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Area <span className="text-destructive">*</span></label>
          <select value={areaId} onChange={(e) => setAreaId(e.target.value)} disabled={!inspectionId || areasLoading}
            className="w-full px-4 py-2.5 rounded-xl border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-60 disabled:cursor-not-allowed">
            <option value="">{!inspectionId ? 'Select inspection first' : (areasLoading ? 'Loading areas…' : 'Select area...')}</option>
            {areas.map((area: Record<string, unknown>) => (
              <option key={area.id} value={area.id}>{area.area_name}{area.area_code ? ` (${area.area_code})` : ''}</option>
            ))}
          </select>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Link to="/ncrs" className="px-4 py-2 rounded-xl border border-input text-foreground font-medium hover:bg-accent transition-colors">Cancel</Link>
          <button onClick={() => createMutation.mutate()} disabled={isPending || !title.trim() || !description.trim() || !inspectionId || !areaId}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary-600 transition-colors disabled:opacity-50">
            {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Create NCR
          </button>
        </div>
      </div>
    </div>
  )
}
